import { normalizeTeamName } from './teamUtils';

/**
 * Gets a player by their ID
 * @param {Array} players - Array of player objects
 * @param {String} playerId - Player ID
 * @returns {Object} Player object
 */
export const getPlayerById = (players, playerId) => {
  if (!players || players.length === 0 || !playerId) return null;
  
  return players.find(player => player.player_id === playerId);
};

/**
 * Gets players by team name
 * @param {Array} players - Array of player objects
 * @param {String} teamName - Team name
 * @returns {Array} Array of player objects
 */
export const getPlayersByTeam = (players, teamName) => {
  if (!players || players.length === 0 || !teamName) {
    return [];
  }
  
  // Get all games for this team using player_team instead of team
  const teamGames = players.filter(player => {
    const playerTeam = player.player_team;
    if (!playerTeam) {
      console.warn('Player missing player_team:', player);
      return false;
    }
    return normalizeTeamName(playerTeam) === normalizeTeamName(teamName);
  });
  
  if (teamGames.length === 0) return [];

  // Find the most recent year
  const years = teamGames.map(p => parseInt(p.year)).filter(Boolean);
  if (years.length === 0) return [];
  const mostRecentYear = Math.max(...years);
  
  // Helper function to convert round to number
  const getRoundNumber = (round) => {
    if (!round) return 0;
    const match = round.match(/\d+/);
    return match ? parseInt(match[0]) : 0;
  };

  // Sort function using year and round
  const sortByYearAndRound = (a, b) => {
    const yearDiff = parseInt(b.year) - parseInt(a.year);
    if (yearDiff !== 0) return yearDiff;
    return getRoundNumber(b.round) - getRoundNumber(a.round);
  };
  
  // Get unique player names from most recent year
  const uniquePlayerNames = [...new Set(
    teamGames
      .filter(player => parseInt(player.year) === mostRecentYear)
      .map(player => player.player_name)
  )];
  
  // Get most recent game for each player
  return uniquePlayerNames
    .map(playerName => {
      const playerGames = teamGames
        .filter(p => p.player_name === playerName && parseInt(p.year) === mostRecentYear)
        .sort(sortByYearAndRound);
      return playerGames[0];
    })
    .filter(Boolean);
};

/**
 * Gets a player's stats for a specific stat category
 * @param {Array} players - Array of player objects
 * @param {String} playerName - Player's full name
 * @param {String} statCategory - Stat category (e.g., 'disposals.default', 'goals.default')
 * @returns {Array} Array of stat values
 */
export const getPlayerStatsByCategory = (players, playerName, statCategory) => {
  if (!players || !playerName || !statCategory) {
    return [];
  }
  
  // Filter games for this player
  const playerGames = players.filter(p => p.player_name === playerName);
  
  // Map to the required format
  const stats = playerGames.map(game => ({
    value: parseFloat(game[statCategory]) || 0,
    date: game.created_at,
    round: game.round,
    opponent: game.opponent,
    is_home: game.is_home
  }));
  
  return stats;
};

/**
 * Calculates a player's average for a specific stat category
 * @param {Array} players - Array of player objects
 * @param {String} playerId - Player ID
 * @param {String} statCategory - Stat category
 * @param {Number} lastNGames - Number of last games to consider (optional)
 * @returns {Number} Average stat value
 */
export const getPlayerStatAverage = (players, playerId, statCategory, lastNGames = null) => {
  if (!players || players.length === 0 || !playerId || !statCategory) return 0;
  
  const playerGames = players.filter(player => player.player_id === playerId);
  
  // Sort games by date (newest first)
  const sortedGames = [...playerGames].sort((a, b) => new Date(b.date) - new Date(a.date));
  
  // Take only the last N games if specified
  const gamesToConsider = lastNGames ? sortedGames.slice(0, lastNGames) : sortedGames;
  
  if (gamesToConsider.length === 0) return 0;
  
  const sum = gamesToConsider.reduce((total, game) => {
    return total + (parseFloat(game[statCategory]) || 0);
  }, 0);
  
  return sum / gamesToConsider.length;
};

/**
 * Gets a player's full name
 * @param {Object} player - Player object
 * @returns {String} Player's full name
 */
export const getPlayerFullName = (player) => {
  if (!player) return '';
  return player.player_name || '';
};

/**
 * Gets a player's team
 * @param {Object} player - Player object
 * @returns {String} Player's team
 */
export const getPlayerTeam = (player) => {
  if (!player) return '';
  
  return player['team.name'];
};

/**
 * Gets players for a specific match
 * @param {Array} players - Array of player objects
 * @param {String} homeTeam - Home team name
 * @param {String} awayTeam - Away team name
 * @returns {Object} Object with home and away team players
 */
export const getPlayersForMatch = (players, homeTeam, awayTeam) => {
  if (!players || players.length === 0 || !homeTeam || !awayTeam) return { home: [], away: [] };
  
  // Get all players from each team
  const homePlayers = players.filter(player => 
    player['team.name'] === homeTeam || 
    (player.teamStatus === 'home' && player.match_home_team === homeTeam)
  );
  
  const awayPlayers = players.filter(player => 
    player['team.name'] === awayTeam || 
    (player.teamStatus === 'away' && player.match_away_team === awayTeam)
  );
  
  // Remove duplicates by player ID
  const uniqueHomePlayers = [...new Map(homePlayers.map(player => 
    [player.player_id, player]
  )).values()];
  
  const uniqueAwayPlayers = [...new Map(awayPlayers.map(player => 
    [player.player_id, player]
  )).values()];
  
  return { 
    home: uniqueHomePlayers, 
    away: uniqueAwayPlayers 
  };
};

export const getPlayerGames = (players, playerName) => {
  if (!players || !playerName) return [];

  // Helper function to convert round to number
  const getRoundNumber = (round) => {
    if (!round) return 0;
    const match = round.match(/\d+/);
    return match ? parseInt(match[0]) : 0;
  };

  // Sort function using year and round
  const sortByYearAndRound = (a, b) => {
    const yearDiff = parseInt(b.year) - parseInt(a.year);
    if (yearDiff !== 0) return yearDiff;
    return getRoundNumber(b.round) - getRoundNumber(a.round);
  };

  return players
    .filter(player => player.player_name === playerName)
    .sort(sortByYearAndRound);
};

/**
 * Gets a player's position from their stats
 * @param {Array} players - Array of player objects
 * @param {String} playerName - Player's full name
 * @returns {String} Player's position
 */
export const getPlayerPosition = (players, playerName) => {
  if (!players || players.length === 0 || !playerName) return null;
  
  // Find the most recent game for this player
  const playerGames = players
    .filter(p => `${p['player.givenName']} ${p['player.surname']}` === playerName)
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  // Return the position from their most recent game
  return playerGames[0]?.position || null;
};

// Update the getPlayerByName function to match our data structure
export const getPlayerByName = (players, playerName) => {
  if (!players || !playerName) return null;
  
  // Helper function to convert round to number
  const getRoundNumber = (round) => {
    if (!round) return 0;
    const match = round.match(/\d+/);
    return match ? parseInt(match[0]) : 0;
  };

  // Sort function using year and round
  const sortByYearAndRound = (a, b) => {
    const yearDiff = parseInt(b.year) - parseInt(a.year);
    if (yearDiff !== 0) return yearDiff;
    return getRoundNumber(b.round) - getRoundNumber(a.round);
  };
  
  const playerGames = players
    .filter(p => p.player_name === playerName)
    .sort(sortByYearAndRound);
  
  return playerGames[0];
};

/**
 * Calculates player performance score based on game stats using custom scoring system
 * @param {Object} player - Player game data object
 * @returns {Number} Performance score
 */
export const calculatePlayerPerformanceScore = (player) => {
  if (!player) return 0;
  
  let score = 0;
  
  // 4pts per point scored
  score += (parseFloat(player['points']) || 0) * 4;
  
  // 10pts per try assist
  score += (parseFloat(player['try_assists']) || 0) * 10;
  
  // 1pt per 10-meter run (rounded down to the nearest 10)
  const runMeters = parseFloat(player['all_run_metres']) || 0;
  score += Math.floor(runMeters / 10);
  
  // 5pts per line break
  score += (parseFloat(player['line_breaks']) || 0) * 5;
  
  // 1pt per tackle
  score += (parseFloat(player['tackles_made']) || 0);
  
  return score;
};

/**
 * Calculates average performance score across multiple games
 * @param {Array} games - Array of player game data objects
 * @param {Number} lastNGames - Number of last games to consider (optional)
 * @returns {Number} Average performance score
 */
export const getAveragePerformanceScore = (games, lastNGames = null) => {
  if (!games || games.length === 0) return 0;
  
  // Sort games by date (newest first) if needed
  const sortedGames = lastNGames ? 
    [...games].sort((a, b) => new Date(b.date) - new Date(a.date)) : 
    games;
  
  // Take only the last N games if specified
  const gamesToConsider = lastNGames ? sortedGames.slice(0, lastNGames) : sortedGames;
  
  if (gamesToConsider.length === 0) return 0;
  
  const totalScore = gamesToConsider.reduce((sum, game) => {
    return sum + calculatePlayerPerformanceScore(game);
  }, 0);
  
  return totalScore / gamesToConsider.length;
}; 